import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/lib/content";
import { useLang } from "@/lib/LanguageContext";
import { MaskLine, Reveal, Eyebrow } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

export default function Manifesto() {
  const { c } = useLang();
  const m = c.manifesto;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section id="manifesto" ref={ref} data-testid="manifesto-section" className="relative py-28 sm:py-40 bg-[#E7E2D1] overflow-hidden">
      <LeafDecor layers={[
        { img: "corner", className: "top-0 right-0 w-[440px] sm:w-[580px] -mt-20 -mr-24 rotate-6", opacity: 0.4 },
        { img: "bunch", className: "bottom-0 left-0 w-[300px] sm:w-[360px] -ml-16 -mb-12 -rotate-12", opacity: 0.3 },
        { img: "side", className: "top-1/3 left-1/3 h-[280px] rotate-[18deg] hidden xl:block", opacity: 0.16 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
        <Eyebrow testid="manifesto-eyebrow">{m.eyebrow}</Eyebrow>

        <div className="mt-10 grid lg:grid-cols-12 gap-y-14 lg:gap-x-16">
          <div className="lg:col-span-7">
            <h2 className="font-serif font-light leading-[1.02] tracking-[-0.02em] text-forest text-[8vw] sm:text-6xl lg:text-[68px]">
              {m.headline.map((l, i) => (
                <MaskLine key={i} delay={i * 0.08} className={l.em ? "text-gold italic" : ""}>
                  {l.text}
                </MaskLine>
              ))}
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={0.2}>
              <p className="font-sans text-base sm:text-lg leading-relaxed text-forest/95 border-l-2 border-gold pl-6">{m.intro}</p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 grid lg:grid-cols-12 gap-x-16 gap-y-16">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="divide-y divide-forest/10 border-t border-forest/10">
              {m.chapters.map((ch, i) => (
                <Reveal key={ch.no} delay={i * 0.05} className="py-8 group">
                  <div className="flex gap-6 sm:gap-10">
                    <span className="font-serif text-3xl sm:text-4xl text-gold/70 group-hover:text-gold transition-colors duration-500 shrink-0">{ch.no}</span>
                    <div>
                      <h3 className="font-serif text-2xl sm:text-3xl text-forest mb-2">{ch.title}</h3>
                      <p className="font-sans text-sm sm:text-[15px] leading-relaxed text-forest/95 max-w-xl">{ch.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative h-[420px] sm:h-[560px] overflow-hidden border border-forest/10 shadow-[0_30px_60px_-30px_rgba(3,22,12,0.4)]">
              <motion.img style={{ y: imgY, scale: 1.15 }} src={IMAGES.farm} alt="Venezuelan plantain plantation" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-7">
                <div className="font-serif italic text-2xl text-cream">{m.captionTitle}</div>
                <div className="font-sans text-xs uppercase tracking-[0.2em] text-gold mt-1">{m.captionSub}</div>
              </div>
            </div>

            <Reveal delay={0.15} className="mt-8">
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                {m.values.map((v) => (
                  <span key={v} className="flex items-center gap-2 font-sans text-xs uppercase tracking-[0.16em] text-forest/95">
                    <span className="rotate-45 inline-block h-1.5 w-1.5 bg-leaf" />
                    {v}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
